import { API } from "./APIClient";
import qs from "qs";
import { getCurrentUserId } from "../utils/reduxUser";
import { downloadFile } from "../utils/downloadFile";

const mapMotivationData = (data = {}) => ({
    id: data?.id,
    salaryPerHour: data?.salary_per_hour ?? 0,
    hoursPerShift: data?.hours_per_shift ?? 0,
    countShiftsMonthly: data?.count_shifts_monthly ?? 0,
    baseSalary: data?.base_salary ?? 0,
    avgRevenue: data?.avg_revenue ?? 0,
    bonusPercentage: data?.bonus_percentage ?? 0,
});

class AdminAPIClass {
    async checkAdmin() {
        const response = await API.get(`/admin/checkAdmin`);

        return response.data;
    }

    async updateAdminName(name) {
        const response = await API.patch(`/admin/updateName`, {
            name,
        });

        return response.data;
    }

    async updateAdminInfo(_adminId, { name }) {
        return await this.updateAdminName(name);
    }

    async fetchAdminInfo() {
        const response = await API.get(`/admin/adminInfo`);

        const cafes = [];
        for (const item of response.data.cafes) {
            cafes.push({
                id: item.id,
                name: item.name,
                group: item.group,
                address: item.address,
                maxCountEmp: item.max_emp_count,
            })
        }

        return {
            id: response.data.id || response.data.user_id,
            profilePhotoUrl: response.data.profile_photo_url,
            name: response.data.name,
            cafes: cafes,
        };
    }

    async changeUserCafe(userId, cafeId) {
        const response = await API.patch(`/admin/users/changeCafe`, {
            employee_id: userId,
            cafe_id: cafeId,
        });

        return response.data;
    }

    async updateCafeInfo(cafeId, name, address, maxEmpCount) {
        const response = await API.patch(`/admin/cafe/updateInfo`, {
            cafe_id: cafeId,
            name: name,
            address: address,
            max_emp_count: maxEmpCount,
        });

        return {
            id: response.data.id,
            name: response.data.name,
            address: response.data.address,
            maxEmpCount: response.data.max_emp_count,
        };
    }

    async fetchCafeEmployees(cafeId) {
        const response = await API.get(`/admin/cafe/${cafeId}/employees`);

        const result = [];
        for (const item of response.data) {
            result.push({
                registeredAt: item.registered_at,
                userId: item.user_id,
                name: item.name,
                cafeId: item.cafe_id,
                level: item.level,
                totalExp: item.total_exp,
                photo: item.profile_photo_url,
            })
        }

        return result;
    }

    async fetchUserInfo(userId) {
        const response = await API.get(`/admin/users/userInfo/${userId}`);

        return {
            userId: response.data.user_id,
            name: response.data.name,
            cafeId: response.data.cafe_id,
            level: response.data.level,
            totalExp: response.data.total_exp,
            kpiSchemeId: response.data.kpi_scheme_id,
            photo: response.data.profile_photo_url,
        };
    }

    async fetchUserAchieves(userId) {
        const response = await API.get(`/admin/users/userAchieves/${userId}`);

        return response.data.achievements;
    }

    async fetchUserMotivation(userId) {
        const response = await API.get(`/admin/motivation/employee/${userId}`);

        return mapMotivationData(response?.data);
    }

    async saveUserMotivation(userId, motivation) {
        const response = await API.put(`/admin/motivation/employee/${userId}`, {
            employee_id: userId,
            salary_per_hour: motivation.salaryPerHour,
            hours_per_shift: motivation.hoursPerShift,
            count_shifts_monthly: motivation.countShiftsMonthly,
            base_salary: motivation.baseSalary,
            avg_revenue: motivation.avgRevenue,
            bonus_percentage: motivation.bonusPercentage,
        })

        return mapMotivationData(response?.data);
    }

    async fetchChecklistID(userId, date = new Date()) {
        const response = await API.get(`/admin/checklist/employee/${userId}/date/${date.toISOString().split("T")[0]}`);

        return {
            id: response.data.id,
            createdAt: response.data.created_at,
            checklistDate: response.data.checklist_date,
        };
    }

    async fetchDailyTasks(userId, checklistId) {
        const query = qs.stringify({
            current_date: new Date().toISOString().split("T")[0],
            employee_id: userId,
        });

        const response = await API.get(`/admin/checklist/${checklistId}/tasks?${query}`);

        const result = [];
        for (const item of response.data.result) {
            result.push({
                id: item.id,
                createdAt: item.created_at,
                checklistId: item.checklist_id,
                employeeId: item.employee_id,
                taskText: item.task_text,
                countTotal: item.count_total,
                countDone: item.count_done,
                rewardExp: item.reward_exp,
                categoryName: item.category_name,
                startDate: item.start_date,
                endDate: item.end_date,
                assigneeDate: item.start_date,
                repeatEveryNDays: item.repeat_every_n_days,
                rewardReceived: item.reward_received,
                taskType: item.task_type,
                priority: item.priority - 1,
            });
        };

        return result;
    }

    async deleteUserFromCafe(cafeId, userId) {
        const query = qs.stringify({
            cafe_id: cafeId,
        })

        const response = await API.delete(`/admin/users/deleteUserFromCafe/${userId}?${query}`);

        return response.data;
    }

    async createTask(cafeId, employees, taskText, taskType, countTotal, rewardExp, beginDate, endDate, repeatEveryNDays, categoryName, priority) {
        const data = [];
        for (const item of employees) {
            try {
                const checklistId = (await this.fetchChecklistID(item)).id;

                data.push({
                    checklist_id: checklistId,
                    employee_id: item,
                    task_text: taskText,
                    task_type: taskType,
                    count_total: countTotal,
                    reward_exp: rewardExp,
                    start_date: beginDate ? beginDate.toISOString().split("T")[0] : null,
                    end_date: endDate ? endDate.toISOString().split("T")[0] : null,
                    repeat_every_n_days: repeatEveryNDays,
                    category_name: categoryName,
                    cafe_id: cafeId,
                    priority: priority + 1
                });
            } catch (error) {
                console.error(error);
            }
        }

        const response = await API.post(`/admin/checklist/tasks/createTaskMultipleEmp`, data);

        return response.data;
    }

    async fetchReccurentTasks(userId) {
        const response = await API.get(`/admin/checklist/tasks/reccurentTasks/${userId}`);

        const result = [];
        for (const item of response.data.result) {
            result.push({
                id: item.id,
                createdAt: item.created_at,
                employeeId: item.employee_id,
                taskText: item.task_text,
                countTotal: item.count_total,
                countDone: item.count_done,
                rewardExp: item.reward_exp,
                categoryName: item.category_name,
                repeatEveryNDays: item.repeat_every_n_days,
            })
        };

        return result;
    }

    async deleteDailyTask(taskId) {
        const response = await API.delete(`/admin/checklist/tasks/${taskId}/deleteDailyTask`);

        return response.data;
    }

    async deleteReccurentTask(taskId) {
        const response = await API.delete(`/admin/checklist/tasks/${taskId}/deleteReccurentTask`);

        return response.data;
    }

    async fetchAdminChecklistID(adminId = getCurrentUserId(), date = new Date().toISOString().split("T")[0]) {
        const response = await API.get(`/admin/checklist/admin/${adminId}/date/${date}`);

        return {
            id: response.data.id,
            adminId: response.data.admin_id,
            checklistDate: response.data.checklist_date,
            createdAt: response.data.created_at,
        };
    }

    async fetchAdminDailyTasks(checklistId, date = new Date().toISOString().split("T")[0]) {
        const query = qs.stringify({
            current_date: date
        });

        const response = await API.get(`/admin/checklist/${checklistId}/adminTasks?${query}`);

        const result = [];
        for (const item of response.data.result) {
            result.push({
                id: item.id,
                createdAt: item.created_at,
                checklistId: item.checklist_id,
                adminId: item.admin_id,
                taskText: item.task_text,
                countTotal: item.count_total,
                countDone: item.count_done,
                assigneeDate: item.assignee_date,
                repeatEveryNDays: item.repeat_every_n_days,
                taskType: item.task_type,
            });
        };

        return result;
    }

    async fetchAdminReccurentTasks(adminId = getCurrentUserId()) {
        const response = await API.get(`/admin/checklist/adminTasks/reccurentTasks/${adminId}`);

        const result = [];
        for (const item of response.data.result) {
            result.push({
                id: item.id,
                createdAt: item.created_at,
                adminId: item.admin_id,
                taskText: item.task_text,
                countTotal: item.count_total,
                countDone: item.count_done,
                repeatEveryNDays: item.repeat_every_n_days,
                taskType: "reccurent",
            })
        };

        return result;
    }

    async createAdminTask(checklistId, taskText, taskType, countTotal, beginDate, endDate, repeatEveryNDays) {
        const adminId = getCurrentUserId();
        const response = await API.post(`/admin/checklist/adminTasks/createTasks`, {
            checklist_id: checklistId,
            admin_id: adminId,
            task_text: taskText,
            task_type: taskType,
            count_total: countTotal,
            begin_date: beginDate ? beginDate.toISOString().split("T")[0] : null,
            end_date: endDate ? endDate.toISOString().split("T")[0] : null,
            repeat_every_n_days: repeatEveryNDays,
        });

        return response.data;
    }

    async addCompleteStep(taskId, taskType, countDone, countTotal) {
        const query = qs.stringify({
            task_type: taskType
        });

        const response = await API.patch(
            `/admin/checklist/adminTasks/${taskId}/complete-step?${query}`,
            {
                count_done: countDone,
                count_total: countTotal,
            },
        );

        return {
            id: response.data.id,
            countDone: response.data.count_done,
            countTotal: response.data.count_total,
        };
    }

    async cancelAdminTaskCompletion(taskId, taskType) {
        const response = await API.post(`/admin/checklist/adminTasks/cancelCompletion`, {
            task_id: taskId,
            task_type: taskType,
        });

        return {
            id: response.data.id,
            countDone: response.data.count_done,
            countTotal: response.data.count_total,
        };
    }

    async deleteAdminReccurentTask(taskId) {
        const response = await API.delete(`/admin/checklist/adminTasks/${taskId}/deleteReccurentTask`);

        return response.data;
    }

    async fetchInviteLink(cafeId, expiresHours = 24) {
        const adminId = getCurrentUserId();
        const response = await API.post(`/admin/cafe/inviteLink`, {
            cafe_id: cafeId,
            admin_id: adminId,
            expires_hours: expiresHours,
        });

        return {
            token: response.data.token,
            inviteLink: response.data.invite_link,
            expiresAt: response.data.expires_at,
        };
    }

    async fetchMaxCountEmp(cafeId) {
        const adminId = getCurrentUserId();
        const response = await API.get(`/cafe/${cafeId}/maxCountEmp?employee_id=${adminId}`);

        return response.data;
    }

    async fetchUserPhoto(userId) {
        return null;
    }

    async uploadReport(cafeId, softwareName, date, file) {
        const query = qs.stringify({
            cafe_id: cafeId,
        })

        const formData = new FormData();


        // formData.append("upload_req", JSON.stringify({
        //     cafe_id: cafeId,
        //     report_date: date.toISOString().split("T")[0],
        //     software_name: softwareName,
        //     original_filename: file.name,
        // }));
        formData.append("cafe_id", cafeId);
        formData.append("report_date", date.toISOString().split("T")[0]);
        formData.append("software_name", softwareName);
        formData.append("original_filename", file.name);

        formData.append("file", file);

        const response = await API.post(`/admin/reports/upload-report?${query}`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });

        const result = typeof response.data === "string"
            ? JSON.parse(response.data)
            : response.data;

        return {
            id: result.id,
            originalFilename: decodeURIComponent(result.original_filename),
            reportDate: result.report_date,
            fileSize: result.file_size,
            checksCount: result.checks_count,
            checksAdded: result.checks_added,
            checksSkipped: result.checks_skipped,
            checksTotalSum: result.checks_total_sum,
            createdAt: result.created_at,
        };
    }

    async fetchReports(cafeId) {
        const response = await API.get(`/admin/reports/list?cafe_id=${cafeId}`);

        const result = [];
        for (const item of response.data.reports) {
            result.push({
                id: item.id,
                originalFilename: decodeURIComponent(item.original_filename),
                reportDate: item.report_date,
                softwareName: item.software_name,
                fileSize: item.file_size,
                checksCount: item.checks_count,
                checksAdded: item.checks_added,
                checksSkipped: item.checks_skipped,
                checksTotalSum: item.checks_total_sum,
                createdAt: item.created_at,
            });
        }

        return result;
    }

    async deleteReport(cafeId, reportId) {
        const response = await API.delete(`/admin/reports/${reportId}?cafe_id=${cafeId}`);

        return response.data;
    }

    async getReportDeletionPreview(cafeId, reportId) {
        const response = await API.get(
            `/admin/reports/${reportId}/deletion-preview?cafe_id=${cafeId}`
        );

        return {
            reportId: response.data.report_id,
            filename: response.data.filename,
            checksCount: response.data.checks_count,
            revenueDecrease: response.data.revenue_decrease,
        };
    }

    async downloadReport(cafeId, reportId) {
        const response = await API.get(`/admin/reports/${reportId}/download?cafe_id=${cafeId}`);

        downloadFile(response.data.url, response.data.filename);
    }

    async downloadMonthlyReport({cafeId, month, year}) {
        const query = qs.stringify({
            cafe_id: cafeId,
            month,
            year,
        });
        const response = await API.get(`/admin/reports/monthly-summary/download/token?${query}`);

        downloadFile(response.data.url, response.data.filename);
    }

    async fetchCafeInfo(cafeId) {
        const response = await API.get(`/admin/cafe/${cafeId}/info`);

        return {
            id: response.data.id,
            name: response.data.name,
            group: response.data.group,
            address: response.data.address,
            maxEmpCount: response.data.max_emp_count,
        }
    }

    async fetchDocsAgreement() {
        const userId = getCurrentUserId();
        const response = await API.get(`/admin/${userId}/checkDocsAgreement`);

        return response.data.is_agreed;
    }

    async updateDocsAgreement() {
        const userId = getCurrentUserId();
        const response = await API.patch(`/admin/${userId}/updateDocsAgreement`);

        return response.data.is_agreed;
    }

    async fetchQuestions(cafeId) {
        const response = await API.get(`/admin/cafe/${cafeId}/faq`);

        const result = [];
        for (const item of response.data.items) {
            result.push({
                id: item.id,
                question: item.question_text,
                answer: item.question_answer,
            });
        }

        return result;
    }

    async createQuestion(cafeId, question, answer) {
        const response = await API.post(`/admin/cafe/${cafeId}/faq`, {
            question_text: question,
            question_answer: answer,
            cafe_id: cafeId,
        });

        return {
            id: response.data.id,
            question: response.data.question_text,
            answer: response.data.question_answer,
        };
    }

    async fetchQuestion(cafeId, id) {
        const response = await API.get(`/admin/cafe/${cafeId}/faq/${id}`);

        return {
            id: response.data.id,
            question: response.data.question_text,
            answer: response.data.question_answer,
        };
    }

    async updateQuestion(cafeId, id, question, answer) {
        const response = await API.patch(`/admin/cafe/${cafeId}/faq/${id}`, {
            question_text: question,
            question_answer: answer,
        });

        return {
            id: response.data.id,
            question: response.data.question_text,
            answer: response.data.question_answer,
        };
    }

    async deleteQuestion(cafeId, id) {
        const response = await API.delete(`/admin/cafe/${cafeId}/faq/${id}`);
        return response.data;
    }

    async uploadChecklistXlsx(cafeId, file, employeeIds, taskType, startDate, endDate, repeatEveryNDays, rewardExp) {
        const formData = new FormData();

        formData.append("file", file);
        formData.append("employee_ids", JSON.stringify(employeeIds));
        formData.append("task_type", taskType);
        formData.append("reward_exp", rewardExp);
        
        if (taskType === "daily") {
            formData.append("start_date", startDate.toISOString().split("T")[0]);
            formData.append("end_date", endDate.toISOString().split("T")[0]);
        } else {
            formData.append("repeat_every_n_days", repeatEveryNDays);
        }

        const response = await API.post(`/admin/checklist/uploadChecklistXlsx?cafe_id=${cafeId}`, formData);

        return response.data;
    }

    async fetchRevenueReportsList(cafeId) {
        const response = await API.get(`/admin/revenue-reports/list?cafe_id=${cafeId}`);

        const result = [];
        for (const item of response.data) {
            result.push({
                id: item.id,
                cafeId: item.cafe_id,
                reportMonth: item.report_month,
                reportYear: item.report_year,
                originalFilename: item.original_filename,
                createdAt: item.created_at,
            });
        }

        return result;
    }

    async fetchRevenueReportDownloadLink(reportId, cafeId) {
        const response = await API.get(`/admin/revenue-reports/download?report_id=${reportId}&cafe_id=${cafeId}`);

        return response.data.url;
    }

    async regenerateRevenueReport(cafeId, month, year) {
        const response = await API.post(
            `/admin/revenue-reports/regenerate?cafe_id=${cafeId}&month=${month}&year=${year}`
        );
        return response.data;
    }

    async downloadComparativeRevenueReport({
        cafeIds,
        startMonth,
        startYear,
        endMonth,
        endYear,
    }) {
        const response = await API.post(
            "/admin/revenue-reports/comparative/file",
            {
                cafe_ids: cafeIds,
                start_month: startMonth,
                start_year: startYear,
                end_month: endMonth,
                end_year: endYear,
            },
            { responseType: "blob" },
        );

        const contentDisposition = response.headers["content-disposition"] || "";
        const filename = (() => {
            const utfFilenameMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
            const regularFilenameMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
            if (utfFilenameMatch?.[1]) {
                return decodeURIComponent(utfFilenameMatch[1]);
            }
            return regularFilenameMatch?.[1] || "comparative_revenue_report.xlsx";
        })();

        const url = URL.createObjectURL(response.data);
        return { url, filename };
    }

    async createMotivationSystem(employeeId, motivation) {
        const response = await API.post(`/admin/motivation/create`, {
            employee_id: employeeId,
            salary_per_hour: motivation.salaryPerHour,
            hours_per_shift: motivation.hoursPerShift,
            count_shifts_monthly: motivation.countShiftsMonthly,
            base_salary: motivation.baseSalary,
            avg_revenue: motivation.avgRevenue,
            bonus_percentage: motivation.bonusPercentage,
        });

        return {
            id: response.data.id,
            employeeId: response.data.employee_id,
            salaryPerHour: response.data.salary_per_hour,
            hoursPerShift: response.data.hours_per_shift,
            countShiftsMonthly: response.data.count_shifts_monthly,
            baseSalary: response.data.base_salary,
            avgRevenue: response.data.avg_revenue,
            bonusPercentage: response.data.bonus_percentage,
            createdAt: response.data.created_at,
        };
    }

    async deleteMotivationSystem(employeeId) {
        const response = await API.delete(`/admin/motivation/employee/${employeeId}`);
        return response.data;
    }

    async fetchMotivationSystemsByCafe(cafeId) {
        const response = await API.get(`/admin/motivation/cafe/${cafeId}`);

        return response.data.map(item => ({
            id: item.id,
            employeeId: item.employee_id,
            salaryPerHour: item.salary_per_hour,
            hoursPerShift: item.hours_per_shift,
            countShiftsMonthly: item.count_shifts_monthly,
            baseSalary: item.base_salary,
            avgRevenue: item.avg_revenue,
            bonusPercentage: item.bonus_percentage,
            createdAt: item.created_at,
        }));
    }

    async fetchCafeMotivations(cafeId) {
        const response = await API.get(`/admin/cafeMotivation/cafe/${cafeId}`);
        return response.data.motivation_list.map(item => ({
            id: item.id,
            cafeId: item.cafe_id,
            description: item.description,
            percentageOfPlan: item.percentage_of_plan,
            employeeRevenue: item.employee_revenue,
            createdAt: item.created_at,
        }));
    }

    async createCafeMotivation(cafeId, condition) {
        const response = await API.post(`/admin/cafeMotivation/cafe/${cafeId}/create`, {
            cafe_id: cafeId,
            description: "123",
            percentage_of_plan: condition.percentageOfPlan,
            employee_revenue: condition.employeeRevenue,
        });
        return {
            id: response.data.id,
            cafeId: response.data.cafe_id,
            description: response.data.description,
            percentageOfPlan: response.data.percentage_of_plan,
            employeeRevenue: response.data.employee_revenue,
            createdAt: response.data.created_at,
        };
    }

    async deleteCafeMotivation(motivationId, cafeId) {
        const response = await API.delete(`/admin/cafeMotivation/${motivationId}?cafe_id=${cafeId}`);
        return response.data;
    }

    async updateCafeMotivation(motivationId, condition) {
        const response = await API.put(
            `/admin/cafeMotivation/${motivationId}?cafe_id=${condition.cafeId}`,
            {
                cafe_id: condition.cafeId,
                description: condition.description,
                percentage_of_plan: condition.percentageOfPlan,
                employee_revenue: condition.employeeRevenue,
            }
        );
        return {
            id: response.data.id,
            cafeId: response.data.cafe_id,
            description: response.data.description,
            percentageOfPlan: response.data.percentage_of_plan,
            employeeRevenue: response.data.employee_revenue,
            createdAt: response.data.created_at,
        };
    }

    async uploadProfilePhoto(photo) {
        const formData = new FormData();
        formData.append("photo", photo);
        
        const response = await API.post(`/admin/profilePhoto`, formData);
        return response.data.profile_photo_url;
    }

    async deleteProfilePhoto() {
        const response = await API.delete(`/admin/profilePhoto`);
        return response.data;
    }
}

const AdminAPI = new AdminAPIClass();
export default AdminAPI;
